import { PROJECT_TYPE_LABELS } from "../types/project-types.js";
import type { EstimatorInput, ExplanationFact, RegionalResolutionResult } from "../types/index.js";

const SCOPE_LABELS: Record<EstimatorInput["scope"], string> = {
  cosmetic_refresh: "Cosmetic refresh",
  mid_range_remodel: "Standard renovation",
  full_gut_renovation: "Full gut renovation",
  custom_high_end: "Custom / high-end renovation"
};

const FINISH_LABELS: Record<EstimatorInput["finishLevel"], string> = {
  budget: "Budget finish level",
  standard: "Standard finish level",
  premium: "Premium finish level",
  luxury: "Luxury finish level"
};

function yesNoLabel(value: "yes" | "no" | "unknown", yesLabel: string, noLabel: string, unknownLabel: string): string {
  if (value === "yes") return yesLabel;
  if (value === "no") return noLabel;
  return unknownLabel;
}

/**
 * Builds the machine-readable "what went into this number" fact list
 * (`EstimatorResult.explanation.inputSummary`). Mirrors the worked
 * example from the Phase 2 brief - a 140 sq. ft. kitchen in Ontario,
 * standard renovation, semi-custom cabinetry, quartz countertops,
 * appliance replacement, plumbing retained, no structural changes -
 * generalized across project types and selections.
 */
export function buildInputSummary(input: EstimatorInput, regionalResolution: RegionalResolutionResult): ExplanationFact[] {
  const facts: ExplanationFact[] = [];

  const projectLabel = PROJECT_TYPE_LABELS[input.projectType] ?? String(input.projectType);
  if (input.roomSize) {
    facts.push({ label: "Project", value: `${input.roomSize.value} ${input.roomSize.unit === "sqft" ? "sq. ft." : "sq. m."} ${projectLabel.toLowerCase()}` });
  } else {
    facts.push({ label: "Project", value: `${projectLabel} (room size not provided)` });
  }

  const regionLabel = input.location.city ? `${input.location.city}, ${input.location.province}` : input.location.province;
  const fallbackNote = regionalResolution.isFallback ? ` (fell back to ${regionalResolution.resolvedTier}-level pricing)` : "";
  facts.push({ label: "Region", value: `${regionLabel} regional pricing${fallbackNote}` });

  facts.push({ label: "Scope", value: SCOPE_LABELS[input.scope] ?? input.scope });
  facts.push({ label: "Finish level", value: FINISH_LABELS[input.finishLevel] ?? input.finishLevel });

  // Project-specific selections, read from rawAnswers when present. Gated
  // on projectType (not just presence of the key) so a stale kitchen_*
  // answer left over from switching project types mid-flow can never leak
  // into a non-kitchen estimate's explanation - see
  // estimator-app/src/state/useEstimatorFlow.ts, which is the primary
  // fix (it no longer carries project-specific answers across a project
  // type switch at all); this is defense in depth for any other caller
  // that hands the engine an EstimatorInput directly.
  if (input.projectType === "kitchen") {
    const cabinetry = input.rawAnswers["kitchen_cabinetry"];
    if (typeof cabinetry === "string") {
      facts.push({ label: "Cabinetry", value: cabinetry.replace(/_/g, " ") });
    }
    const countertop = input.rawAnswers["kitchen_countertop"];
    if (typeof countertop === "string") {
      facts.push({ label: "Countertops", value: countertop.replace(/_/g, " ") });
    }
    const appliances = input.rawAnswers["kitchen_appliances"];
    if (typeof appliances === "string") {
      facts.push({ label: "Appliances", value: appliances.replace(/_/g, " ") });
    }
    const flooring = input.rawAnswers["kitchen_flooring_type"];
    if (typeof flooring === "string") {
      facts.push({ label: "Flooring", value: flooring.replace(/_/g, " ") });
    }
    const backsplash = input.rawAnswers["kitchen_backsplash_type"];
    if (typeof backsplash === "string") {
      facts.push({ label: "Backsplash", value: backsplash.replace(/_/g, " ") });
    }
    const island = input.rawAnswers["kitchen_island"];
    if (typeof island === "string") {
      facts.push({ label: "Island", value: island.replace(/_/g, " ") });
    }
  }

  if (input.projectType === "basement" || input.projectType === "basement_suite") {
    const condition = input.rawAnswers["basement_current_condition"];
    if (typeof condition === "string") {
      facts.push({ label: "Current condition", value: condition.replace(/_/g, " ") });
    }
    const rooms = input.rawAnswers["basement_rooms"];
    if (Array.isArray(rooms) && rooms.length > 0) {
      facts.push({ label: "Rooms", value: rooms.map((r) => String(r).replace(/_/g, " ")).join(", ") });
    }
    const bathroomScope = input.rawAnswers["basement_bathroom_scope"];
    if (typeof bathroomScope === "string" && bathroomScope !== "none") {
      facts.push({ label: "Bathroom", value: bathroomScope.replace(/_/g, " ") });
    }
    const kitchenScope = input.rawAnswers["basement_kitchen_scope"];
    if (typeof kitchenScope === "string" && kitchenScope !== "none") {
      facts.push({ label: "Kitchen/wet bar", value: kitchenScope.replace(/_/g, " ") });
    }
    const egressStatus = input.rawAnswers["basement_egress_status"];
    if (typeof egressStatus === "string") {
      facts.push({ label: "Egress", value: egressStatus.replace(/_/g, " ") });
    }
  }

  if (input.projectType === "deck_patio") {
    const kind = input.rawAnswers["deck_patio_kind"];
    if (typeof kind === "string") {
      facts.push({ label: "Kind", value: kind });
    }
    const constructionType = input.rawAnswers["deck_patio_construction_type"];
    if (typeof constructionType === "string") {
      facts.push({ label: "Construction type", value: constructionType.replace(/_/g, " ") });
    }
    const material = input.rawAnswers["deck_patio_material"] ?? input.rawAnswers["deck_patio_patio_material"];
    if (typeof material === "string") {
      facts.push({ label: "Material", value: material.replace(/_/g, " ") });
    }
    const elevation = input.rawAnswers["deck_patio_elevation"];
    if (typeof elevation === "string") {
      facts.push({ label: "Elevation", value: elevation.replace(/_/g, " ") });
    }
    const stairs = input.rawAnswers["deck_patio_stairs"];
    if (typeof stairs === "string" && stairs !== "none") {
      facts.push({ label: "Stairs", value: stairs.replace(/_/g, " ") });
    }
    const railings = input.rawAnswers["deck_patio_railings"];
    if (typeof railings === "string" && railings !== "none") {
      facts.push({ label: "Railings", value: railings.replace(/_/g, " ") });
    }
    const levels = input.rawAnswers["deck_patio_levels"];
    if (typeof levels === "string" && levels !== "single_level") {
      facts.push({ label: "Levels", value: levels.replace(/_/g, " ") });
    }
    const roofCover = input.rawAnswers["deck_patio_roof_cover"];
    if (typeof roofCover === "string" && roofCover !== "none") {
      facts.push({ label: "Roof/cover", value: roofCover.replace(/_/g, " ") });
    }
  }

  // Ensuite-only facts (not shared with bathroom - see rules/ensuite.ts's two
  // additive-only categories). Strictly gated on projectType === "ensuite" (not
  // bathroom, which shares the same rawAnswers shape via calculateBathroom) so
  // these never leak into a plain bathroom's input summary.
  if (input.projectType === "ensuite") {
    const freestandingTub = input.rawAnswers["ensuite_freestanding_tub"];
    if (typeof freestandingTub === "string" && freestandingTub !== "none") {
      facts.push({ label: "Freestanding tub", value: freestandingTub });
    }
    const layoutComplexity = input.rawAnswers["ensuite_layout_complexity"];
    if (typeof layoutComplexity === "string" && layoutComplexity !== "simple") {
      facts.push({ label: "Layout complexity", value: layoutComplexity.replace(/_/g, " ") });
    }
  }

  facts.push({
    label: "Plumbing",
    value: yesNoLabel(input.workType.plumbingChanges, "Plumbing changes included", "Plumbing retained", "Plumbing scope unconfirmed")
  });
  facts.push({
    label: "Electrical",
    value: yesNoLabel(input.workType.electricalChanges, "Electrical changes included", "Electrical retained", "Electrical scope unconfirmed")
  });
  facts.push({
    label: "Structural",
    value: yesNoLabel(input.workType.structuralChanges, "Structural changes included", "No structural changes", "Structural scope unconfirmed")
  });
  facts.push({
    label: "Demolition",
    value: yesNoLabel(input.workType.demolitionRequired, "Demolition included", "No demolition", "Demolition scope unconfirmed")
  });
  facts.push({
    label: "Accessibility",
    value: yesNoLabel(input.accessibility.required, "Accessibility accommodations included", "No accessibility accommodations", "Accessibility needs unconfirmed")
  });
  facts.push({
    label: "Permits/design",
    value:
      input.designAndPermits.needsDesignServices === "yes"
        ? "Design services requested"
        : yesNoLabel(input.designAndPermits.permitLikelyRequired, "Permit likely required", "No permit expected", "Permit requirement unconfirmed")
  });

  return facts;
}

const CABINETRY_CLAUSES: Record<string, string> = {
  keep_existing: "keeping the existing cabinets",
  reface_existing: "refacing the existing cabinets",
  stock_new: "new stock cabinetry",
  semi_custom_new: "semi-custom cabinetry",
  full_custom_new: "custom cabinetry"
};

const COUNTERTOP_CLAUSES: Record<string, string> = {
  keep_existing: "keeping the existing countertops",
  laminate: "laminate countertops",
  quartz: "quartz countertops",
  granite: "granite countertops",
  premium_custom: "premium/custom countertops",
  undecided: "countertops not yet decided"
};

const APPLIANCE_CLAUSES: Record<string, string> = {
  keep_existing: "keeping the existing appliances",
  replace_some: "replacing some appliances",
  replace_all: "replacing all appliances",
  premium: "a premium appliance package"
};

/**
 * Builds a plain-language, selections-driven explanation sentence for a
 * kitchen result - per the Phase 5A brief, "Do not use generic
 * explanations that ignore the user's selections," with a worked example
 * ("Your estimate is based on a 120-180 sq. ft. kitchen in Ontario with a
 * standard renovation scope, semi-custom cabinetry, quartz countertops,
 * replacement appliances, updated lighting, and no major plumbing or
 * structural changes."). This walks the actual answers and only mentions
 * a category when it was actually selected/materially affects the
 * number - a skipped/defaulted category is left out of the sentence
 * rather than stated as if it were a deliberate choice.
 */
export function buildKitchenPlainLanguageSummary(input: EstimatorInput): string {
  const clauses: string[] = [];

  const cabinetry = input.rawAnswers["kitchen_cabinetry"];
  if (typeof cabinetry === "string" && CABINETRY_CLAUSES[cabinetry]) clauses.push(CABINETRY_CLAUSES[cabinetry]!);

  const countertop = input.rawAnswers["kitchen_countertop"];
  if (typeof countertop === "string" && COUNTERTOP_CLAUSES[countertop]) clauses.push(COUNTERTOP_CLAUSES[countertop]!);

  const appliances = input.rawAnswers["kitchen_appliances"];
  if (typeof appliances === "string" && APPLIANCE_CLAUSES[appliances]) clauses.push(APPLIANCE_CLAUSES[appliances]!);

  const electricalScope = input.rawAnswers["kitchen_electrical_scope"];
  if (typeof electricalScope === "string") {
    clauses.push(electricalScope === "new_lighting" ? "updated lighting" : electricalScope.replace(/_/g, " "));
  } else if (input.workType.electricalChanges === "no") {
    clauses.push("no electrical changes");
  }

  const plumbingKnown = input.workType.plumbingChanges === "no";
  const structuralKnown = input.workType.structuralChanges === "no";
  if (plumbingKnown && structuralKnown) {
    clauses.push("no major plumbing or structural changes");
  } else {
    if (plumbingKnown) clauses.push("no plumbing changes");
    if (structuralKnown) clauses.push("no structural changes");
  }

  const sizeLabel = input.roomSize ? `${input.roomSize.value} sq. ft.` : "unspecified-size";
  const scopeLabel = (SCOPE_LABELS[input.scope] ?? input.scope).toLowerCase();

  const lead = `Your estimate is based on a ${sizeLabel} kitchen in ${input.location.province} with a ${scopeLabel} scope`;
  if (clauses.length === 0) return `${lead}.`;

  const joined = clauses.length === 1 ? clauses[0] : `${clauses.slice(0, -1).join(", ")}, and ${clauses[clauses.length - 1]}`;
  return `${lead}, ${joined}.`;
}

const CONDITION_CLAUSES: Record<string, string> = {
  unfinished: "an unfinished space",
  partially_finished: "a partially-finished space",
  previously_finished_renovating: "a previously-finished space being renovated"
};

const BATHROOM_CLAUSES: Record<string, string> = {
  existing_renovation: "renovating the existing bathroom",
  new_bathroom: "adding a new bathroom"
};

const KITCHEN_CLAUSES: Record<string, string> = {
  wet_bar: "a wet bar",
  kitchenette: "a kitchenette",
  full_kitchen: "a full second kitchen"
};

const ROOM_LABELS: Record<string, string> = {
  rec_area: "an open recreation area",
  bedroom: "a bedroom",
  office: "a home office",
  storage: "storage space",
  other: "other room(s)"
};

/**
 * Builds a plain-language, selections-driven explanation sentence for a
 * basement result - same pattern and rationale as
 * `buildKitchenPlainLanguageSummary` above (Phase 5A): only mentions a
 * category when it was actually selected/materially affects the number,
 * so a skipped/defaulted category is left out rather than stated as if
 * it were a deliberate choice.
 */
export function buildBasementPlainLanguageSummary(input: EstimatorInput): string {
  const clauses: string[] = [];

  const condition = input.rawAnswers["basement_current_condition"];
  const conditionClause = typeof condition === "string" ? CONDITION_CLAUSES[condition] : undefined;

  const rooms = input.rawAnswers["basement_rooms"];
  if (Array.isArray(rooms) && rooms.length > 0) {
    const roomLabels = rooms.filter((r): r is string => typeof r === "string").map((r) => ROOM_LABELS[r] ?? r.replace(/_/g, " "));
    if (roomLabels.length > 0) {
      clauses.push(roomLabels.length === 1 ? roomLabels[0]! : `${roomLabels.slice(0, -1).join(", ")}, and ${roomLabels[roomLabels.length - 1]}`);
    }
  }

  const bathroomScope = input.rawAnswers["basement_bathroom_scope"];
  if (typeof bathroomScope === "string" && BATHROOM_CLAUSES[bathroomScope]) clauses.push(BATHROOM_CLAUSES[bathroomScope]!);

  const kitchenScope = input.rawAnswers["basement_kitchen_scope"];
  if (typeof kitchenScope === "string" && KITCHEN_CLAUSES[kitchenScope]) clauses.push(KITCHEN_CLAUSES[kitchenScope]!);

  const egressStatus = input.rawAnswers["basement_egress_status"];
  if (egressStatus === "new_or_modified_required") clauses.push("a possible new/modified egress window");

  const plumbingKnown = input.workType.plumbingChanges === "no";
  const structuralKnown = input.workType.structuralChanges === "no";
  if (plumbingKnown && structuralKnown) {
    clauses.push("no major plumbing or structural changes");
  } else {
    if (plumbingKnown) clauses.push("no plumbing changes");
    if (structuralKnown) clauses.push("no structural changes");
  }

  const sizeLabel = input.roomSize ? `${input.roomSize.value} sq. ft.` : "unspecified-size";
  const scopeLabel = (SCOPE_LABELS[input.scope] ?? input.scope).toLowerCase();
  const conditionPart = conditionClause ? `, ${conditionClause},` : "";

  const lead = `Your estimate is based on a ${sizeLabel} basement${conditionPart} with a ${scopeLabel} scope`;
  if (clauses.length === 0) return `${lead}.`;

  const joined = clauses.length === 1 ? clauses[0] : `${clauses.slice(0, -1).join(", ")}, and ${clauses[clauses.length - 1]}`;
  return `${lead}, including ${joined}.`;
}

const SUITE_BEDROOM_LABELS: Record<string, string> = {
  "0": "no bedroom",
  "1": "1 bedroom",
  "2": "2 bedrooms",
  "3_plus": "3+ bedrooms"
};

const SUITE_BATHROOM_CLAUSES: Record<string, string> = {
  existing_renovation: "renovating the existing bathroom",
  new_bathroom: "a brand-new full bathroom"
};

const SUITE_KITCHEN_CLAUSES: Record<string, string> = {
  kitchenette: "a kitchenette",
  full_kitchen: "a full kitchen"
};

const SUITE_ENTRANCE_CLAUSES: Record<string, string> = {
  new_entrance_needed: "a new separate entrance",
  modify_existing: "modifying an existing entrance into a separate one",
  shared_entrance_only: "a shared entrance only"
};

/**
 * Builds a plain-language, selections-driven explanation sentence for a
 * basement_suite result - deliberately DISTINCT from
 * `buildBasementPlainLanguageSummary` above (Phase 9 brief: a basement
 * suite is a self-contained secondary dwelling unit, not a finished
 * basement with a different label - see rules/basement-suite.ts's doc
 * comment). Surfaces the suite-defining selections (bedroom count,
 * kitchen, bathroom, egress, entrance) and always ends with a
 * non-guarantee compliance line, matching rules/basement-suite.ts's own
 * "basement_suite_compliance_not_guaranteed" warning - the plain-language
 * summary is exactly the kind of place a homeowner could otherwise walk
 * away with a false impression of legal compliance.
 */
export function buildBasementSuitePlainLanguageSummary(input: EstimatorInput): string {
  const clauses: string[] = [];

  const bedroomCount = input.rawAnswers["basement_suite_bedroom_count"];
  const bedroomLabel = typeof bedroomCount === "string" ? SUITE_BEDROOM_LABELS[bedroomCount] : undefined;
  if (bedroomLabel) clauses.push(bedroomLabel);

  const kitchenScope = input.rawAnswers["basement_suite_kitchen_scope"];
  if (typeof kitchenScope === "string" && SUITE_KITCHEN_CLAUSES[kitchenScope]) clauses.push(SUITE_KITCHEN_CLAUSES[kitchenScope]!);

  const bathroomScope = input.rawAnswers["basement_suite_bathroom_scope"];
  if (typeof bathroomScope === "string" && SUITE_BATHROOM_CLAUSES[bathroomScope]) clauses.push(SUITE_BATHROOM_CLAUSES[bathroomScope]!);

  const egressStatus = input.rawAnswers["basement_suite_egress_status"];
  if (egressStatus === "new_or_modified_required") clauses.push("a new/modified egress window");
  else if (egressStatus === "unknown") clauses.push("egress still to be confirmed");

  const entranceStatus = input.rawAnswers["basement_suite_separate_entrance"];
  if (typeof entranceStatus === "string" && SUITE_ENTRANCE_CLAUSES[entranceStatus]) clauses.push(SUITE_ENTRANCE_CLAUSES[entranceStatus]!);

  const sizeLabel = input.roomSize ? `${input.roomSize.value} sq. ft.` : "unspecified-size";
  const scopeLabel = (SCOPE_LABELS[input.scope] ?? input.scope).toLowerCase();

  const lead = `Your estimate is based on a ${sizeLabel} basement suite in ${input.location.province} with a ${scopeLabel} scope`;
  const body = clauses.length === 0 ? `${lead}.` : `${lead}, including ${clauses.length === 1 ? clauses[0] : `${clauses.slice(0, -1).join(", ")}, and ${clauses[clauses.length - 1]}`}.`;

  // Always ends with the same non-guarantee compliance line, regardless of intent -
  // see the doc comment above and rules/basement-suite.ts's own compliance warning.
  return `${body} This is an estimate for a basement suite - it does not mean, and is not a determination of, whether this space can legally become a second dwelling unit; that depends on your municipality's zoning bylaw and a code/inspection review this questionnaire cannot perform.`;
}

const DECK_MATERIAL_CLAUSES: Record<string, string> = {
  pressure_treated: "pressure-treated wood decking",
  cedar: "cedar decking",
  composite: "composite decking",
  premium_custom: "premium/custom decking"
};

const PATIO_MATERIAL_CLAUSES: Record<string, string> = {
  concrete: "a poured concrete surface",
  pavers: "a paver surface",
  natural_stone: "a natural stone surface",
  premium_custom: "a premium/custom surface"
};

const STAIRS_CLAUSES: Record<string, string> = {
  one_set: "one set of stairs",
  two_or_more_sets: "two or more sets of stairs"
};

const RAILING_CLAUSES: Record<string, string> = {
  standard: "standard railings",
  premium_glass_cable: "premium glass/cable railings"
};

const LEVELS_CLAUSES: Record<string, string> = {
  two_levels: "two levels",
  three_plus_levels: "three or more levels"
};

const ROOF_CLAUSES: Record<string, string> = {
  pergola: "a pergola cover",
  solid_roof: "a solid roof/cover"
};

/**
 * Builds a plain-language, selections-driven explanation sentence for a
 * deck/patio result - same pattern and rationale as
 * `buildKitchenPlainLanguageSummary`/`buildBasementPlainLanguageSummary`
 * above: only mentions a category when it was actually
 * selected/materially affects the number, so a skipped/defaulted
 * category is left out rather than stated as if it were a deliberate
 * choice. Distinguishes deck from patio throughout, per the Phase 11
 * brief's "do not use interior renovation assumptions" - there is no
 * generic "renovation" language here at all.
 */
export function buildDeckPatioPlainLanguageSummary(input: EstimatorInput): string {
  const clauses: string[] = [];

  const kind = input.rawAnswers["deck_patio_kind"];
  const isDeck = kind !== "patio";
  const kindLabel = isDeck ? "deck" : "patio";

  const material = isDeck ? input.rawAnswers["deck_patio_material"] : input.rawAnswers["deck_patio_patio_material"];
  const materialClauses = isDeck ? DECK_MATERIAL_CLAUSES : PATIO_MATERIAL_CLAUSES;
  if (typeof material === "string" && materialClauses[material]) clauses.push(materialClauses[material]!);

  if (isDeck) {
    const elevation = input.rawAnswers["deck_patio_elevation"];
    if (elevation === "elevated") clauses.push("an elevated structure");
  }

  const stairs = input.rawAnswers["deck_patio_stairs"];
  if (typeof stairs === "string" && STAIRS_CLAUSES[stairs]) clauses.push(STAIRS_CLAUSES[stairs]!);

  const railings = input.rawAnswers["deck_patio_railings"];
  if (typeof railings === "string" && RAILING_CLAUSES[railings]) clauses.push(RAILING_CLAUSES[railings]!);

  const levels = input.rawAnswers["deck_patio_levels"];
  if (typeof levels === "string" && LEVELS_CLAUSES[levels]) clauses.push(LEVELS_CLAUSES[levels]!);

  const roofCover = input.rawAnswers["deck_patio_roof_cover"];
  if (typeof roofCover === "string" && ROOF_CLAUSES[roofCover]) clauses.push(ROOF_CLAUSES[roofCover]!);

  const constructionType = input.rawAnswers["deck_patio_construction_type"];
  const sizeLabel = input.roomSize ? `${input.roomSize.value} sq. ft.` : "unspecified-size";
  const scopeLabel = (SCOPE_LABELS[input.scope] ?? input.scope).toLowerCase();
  const constructionLabel =
    constructionType === "replacement" ? `replacing an existing ${sizeLabel} ${kindLabel}` : `new construction of a ${sizeLabel} ${kindLabel}`;

  const lead = `Your estimate is based on ${constructionLabel} in ${input.location.province} with a ${scopeLabel} scope`;
  if (clauses.length === 0) return `${lead}.`;

  const joined = clauses.length === 1 ? clauses[0] : `${clauses.slice(0, -1).join(", ")}, and ${clauses[clauses.length - 1]}`;
  return `${lead}, including ${joined}.`;
}

/**
 * Corrected during the Phase 20 audit: this used to be keyed on
 * "bathroom_shower_type", which is not a real question id (the actual
 * questions are "bathroom_existing_configuration" and
 * "bathroom_shower_scope" - see packages/config/src/questions/bathroom.ts)
 * so this clause could never actually appear in a bathroom summary. Keyed
 * on the real `bathroom_shower_scope` answer instead, which describes what's
 * actually happening with the tub/shower - the more meaningful thing to
 * surface in a plain-language summary than the pre-renovation configuration
 * anyway. "keep_existing" is deliberately omitted (nothing changing there
 * isn't a cost driver worth naming).
 */
const SHOWER_SCOPE_CLAUSES: Record<string, string> = {
  replace_like_for_like: "a like-for-like tub/shower replacement",
  upgrade: "an upgraded tub/shower",
  custom_shower: "a custom shower",
  premium_custom: "a premium custom tub/shower solution"
};

/**
 * Builds a plain-language, selections-driven explanation sentence for a
 * bathroom result - same pattern and rationale as
 * `buildKitchenPlainLanguageSummary`/`buildBasementPlainLanguageSummary`/
 * `buildDeckPatioPlainLanguageSummary` above. Bathroom has no dedicated
 * question set yet (see rules/bathroom.ts), so this draws on the fields
 * that ARE captured for it (shower/tub scope, heated floor, accessibility,
 * plumbing/electrical/structural) rather than falling back to the
 * generic "N pricing line items were applied" summary, which is not
 * plain language and does not reflect the user's actual selections.
 *
 * Also used for ensuite (see engine.ts's summary dispatch) - ensuite
 * delegates its entire shared cost model to calculateBathroom (see
 * rules/ensuite.ts), so the same selections drive its summary too; the
 * ensuite-only additions (freestanding tub, layout complexity) don't yet
 * have their own clauses here, matching how kitchen/basement's summaries
 * cover their core selections without enumerating every possible add-on.
 */
const FREESTANDING_TUB_SUMMARY_CLAUSES: Record<string, string> = {
  standard: "a standard freestanding tub",
  premium: "a premium freestanding tub"
};

const LAYOUT_COMPLEXITY_SUMMARY_CLAUSES: Record<string, string> = {
  compartmentalized: "a compartmentalized layout",
  dual_walkway_complex: "a dual-walkway/complex layout"
};

const VANITY_SUMMARY_CLAUSES: Record<string, string> = {
  double_vanity: "a double vanity",
  semi_custom: "a semi-custom vanity",
  custom: "a custom vanity"
};

export function buildBathroomPlainLanguageSummary(input: EstimatorInput): string {
  const clauses: string[] = [];

  const showerScope = input.rawAnswers["bathroom_shower_scope"];
  if (typeof showerScope === "string" && SHOWER_SCOPE_CLAUSES[showerScope]) clauses.push(SHOWER_SCOPE_CLAUSES[showerScope]!);

  // Ensuite-only add-ons (see rules/ensuite.ts) - only ever set when input.projectType
  // is "ensuite", so these are silently absent for a bathroom's rawAnswers.
  const freestandingTub = input.rawAnswers["ensuite_freestanding_tub"];
  if (typeof freestandingTub === "string" && FREESTANDING_TUB_SUMMARY_CLAUSES[freestandingTub]) {
    clauses.push(FREESTANDING_TUB_SUMMARY_CLAUSES[freestandingTub]!);
  }
  const layoutComplexity = input.rawAnswers["ensuite_layout_complexity"];
  if (typeof layoutComplexity === "string" && LAYOUT_COMPLEXITY_SUMMARY_CLAUSES[layoutComplexity]) {
    clauses.push(LAYOUT_COMPLEXITY_SUMMARY_CLAUSES[layoutComplexity]!);
  }

  const vanity = input.rawAnswers["bathroom_vanity"];
  if (typeof vanity === "string" && VANITY_SUMMARY_CLAUSES[vanity]) clauses.push(VANITY_SUMMARY_CLAUSES[vanity]!);

  if (input.rawAnswers["bathroom_heated_floor"] === true) clauses.push("in-floor heating (heated flooring)");

  if (input.accessibility.required === "yes") clauses.push("accessibility/mobility accommodations");

  const plumbingKnown = input.workType.plumbingChanges === "no";
  const structuralKnown = input.workType.structuralChanges === "no";
  if (plumbingKnown && structuralKnown) {
    clauses.push("no major plumbing or structural changes");
  } else {
    if (plumbingKnown) clauses.push("no plumbing changes");
    if (structuralKnown) clauses.push("no structural changes");
  }

  const sizeLabel = input.roomSize ? `${input.roomSize.value} sq. ft.` : "unspecified-size";
  const scopeLabel = (SCOPE_LABELS[input.scope] ?? input.scope).toLowerCase();
  // Project-type-aware rather than hardcoded "bathroom" - this function is shared with ensuite
  // (see the doc comment above), which must not be described as a bathroom in its own summary.
  const projectLabel = (PROJECT_TYPE_LABELS[input.projectType] ?? String(input.projectType)).toLowerCase();

  const lead = `Your estimate is based on a ${sizeLabel} ${projectLabel} in ${input.location.province} with a ${scopeLabel} scope`;
  if (clauses.length === 0) return `${lead}.`;

  const joined = clauses.length === 1 ? clauses[0] : `${clauses.slice(0, -1).join(", ")}, and ${clauses[clauses.length - 1]}`;
  return `${lead}, ${joined}.`;
}

/**
 * The ten Phase 13A interior room project types, all sharing one
 * calculation mechanism (`createInteriorRoomCalculator` in
 * rules/interior-room-shared.ts) and, per this function, one plain-language
 * summary shape. Kept here rather than in project-types.ts since it's a
 * calculation-layer grouping, not a fact about the project type itself.
 */
const INTERIOR_ROOM_PROJECT_TYPES: ReadonlySet<EstimatorInput["projectType"]> = new Set([
  "living_room",
  "family_room",
  "dining_room",
  "bedroom",
  "primary_bedroom",
  "home_office",
  "laundry_room",
  "mudroom",
  "entryway_foyer",
  "hallway"
]);

export function isInteriorRoomProjectType(projectType: EstimatorInput["projectType"]): boolean {
  return INTERIOR_ROOM_PROJECT_TYPES.has(projectType);
}

const INTERIOR_ROOM_FLOORING_CLAUSES: Record<string, string> = {
  standard: "standard flooring",
  premium: "premium flooring"
};

const INTERIOR_ROOM_PAINTING_CLAUSES: Record<string, string> = {
  walls_only: "painting the walls",
  full_room: "painting the full room (walls, trim, and ceiling)"
};

/**
 * Builds a plain-language, selections-driven explanation sentence shared by
 * all ten interior room project types (living room, family room, dining
 * room, bedroom, primary bedroom, home office, laundry room, mudroom,
 * entryway/foyer, hallway) - same pattern and rationale as
 * `buildKitchenPlainLanguageSummary`/`buildBasementPlainLanguageSummary`/
 * `buildDeckPatioPlainLanguageSummary`/`buildBathroomPlainLanguageSummary`
 * above. One function covers all ten (rather than one each) because their
 * shared calculator (`interior-room-shared.ts`) already prices them through
 * the same tiered-feature/boolean-feature shape - the question ids just
 * differ by a `${projectType}_` prefix, so the same lookups work for all
 * ten without hand-writing ten near-identical summary functions.
 */
export function buildInteriorRoomPlainLanguageSummary(input: EstimatorInput): string {
  const projectType = input.projectType;
  const prefix = projectType;
  const clauses: string[] = [];

  const flooring = input.rawAnswers[`${prefix}_flooring_type`];
  if (typeof flooring === "string" && INTERIOR_ROOM_FLOORING_CLAUSES[flooring]) {
    clauses.push(INTERIOR_ROOM_FLOORING_CLAUSES[flooring]!);
  }

  const painting = input.rawAnswers[`${prefix}_painting`];
  if (typeof painting === "string" && INTERIOR_ROOM_PAINTING_CLAUSES[painting]) {
    clauses.push(INTERIOR_ROOM_PAINTING_CLAUSES[painting]!);
  }

  if (input.rawAnswers[`${prefix}_trim`] && input.rawAnswers[`${prefix}_trim`] !== "none") clauses.push("trim work");
  if (input.rawAnswers[`${prefix}_lighting`] && input.rawAnswers[`${prefix}_lighting`] !== "none") clauses.push("lighting upgrades");
  if (input.rawAnswers[`${prefix}_built_ins`] && input.rawAnswers[`${prefix}_built_ins`] !== "none") clauses.push("built-ins/storage");
  if (input.rawAnswers[`${prefix}_windows_doors`] && input.rawAnswers[`${prefix}_windows_doors`] !== "none") clauses.push("windows/doors work");

  if (input.accessibility.required === "yes") clauses.push("accessibility/mobility accommodations");

  const projectLabel = (PROJECT_TYPE_LABELS[projectType] ?? String(projectType)).toLowerCase();
  const sizeLabel = input.roomSize ? `${input.roomSize.value} sq. ft.` : "unspecified-size";
  const scopeLabel = (SCOPE_LABELS[input.scope] ?? input.scope).toLowerCase();

  const lead = `Your estimate is based on a ${sizeLabel} ${projectLabel} in ${input.location.province} with a ${scopeLabel} scope`;
  if (clauses.length === 0) return `${lead}.`;

  const joined = clauses.length === 1 ? clauses[0] : `${clauses.slice(0, -1).join(", ")}, and ${clauses[clauses.length - 1]}`;
  return `${lead}, including ${joined}.`;
}

const ADDITION_STOREYS_CLAUSES: Record<string, string> = {
  two_storey: "a two-storey addition",
  second_storey_addition: "a second storey built over existing structure"
};

const ADDITION_FOUNDATION_CLAUSES: Record<string, string> = {
  slab_on_grade: "a slab-on-grade foundation",
  crawl_space: "a crawl space foundation",
  full_foundation_basement: "a full foundation/basement below it",
  unsure: "a foundation type still to be confirmed"
};

const ADDITION_KITCHEN_CLAUSES: Record<string, string> = {
  renovate_existing: "extending/renovating the adjacent existing kitchen",
  new_kitchen: "a brand-new kitchen"
};

/**
 * Phase 20 audit fix: `addition` had no dedicated summary builder at all and
 * was falling through to the generic "N pricing line item(s)" fallback -
 * same category of gap as the pre-fix bathroom summary above. Follows the
 * same pattern: lead sentence plus selection-driven clauses, only mentioning
 * a category when it was actually selected. `addition_storeys` and
 * `addition_foundation_type` are the two biggest cost/risk drivers per
 * rules/addition.ts, so they're always surfaced when set to anything other
 * than the "simple/no extra cost" default option (single_storey /
 * none_existing_structure respectively, which aren't worth naming since
 * nothing about them is a cost driver worth calling out).
 */
export function buildAdditionPlainLanguageSummary(input: EstimatorInput): string {
  const clauses: string[] = [];

  const storeys = input.rawAnswers["addition_storeys"];
  if (typeof storeys === "string" && ADDITION_STOREYS_CLAUSES[storeys]) clauses.push(ADDITION_STOREYS_CLAUSES[storeys]!);

  const foundation = input.rawAnswers["addition_foundation_type"];
  if (typeof foundation === "string" && ADDITION_FOUNDATION_CLAUSES[foundation]) clauses.push(ADDITION_FOUNDATION_CLAUSES[foundation]!);

  const structuralComplexity = input.rawAnswers["addition_structural_complexity"];
  if (structuralComplexity === "moderate") clauses.push("a moderate structural tie-in");
  else if (structuralComplexity === "complex") clauses.push("a complex structural tie-in");

  const kitchenScope = input.rawAnswers["addition_kitchen_scope"];
  if (typeof kitchenScope === "string" && ADDITION_KITCHEN_CLAUSES[kitchenScope]) clauses.push(ADDITION_KITCHEN_CLAUSES[kitchenScope]!);

  const bathroomScope = input.rawAnswers["addition_bathroom_scope"];
  if (bathroomScope === "one_bathroom") clauses.push("one bathroom");
  else if (bathroomScope === "multiple_bathrooms") clauses.push("two or more bathrooms");

  const bedrooms = input.rawAnswers["addition_bedrooms"];
  if (typeof bedrooms === "number" && bedrooms > 0) {
    clauses.push(`${bedrooms} bedroom${bedrooms === 1 ? "" : "s"}`);
  }

  if (input.accessibility.required === "yes") clauses.push("accessibility/mobility accommodations");

  const sizeLabel = input.roomSize ? `${input.roomSize.value} sq. ft.` : "unspecified-size";
  const scopeLabel = (SCOPE_LABELS[input.scope] ?? input.scope).toLowerCase();

  const lead = `Your estimate is based on a ${sizeLabel} addition in ${input.location.province} with a ${scopeLabel} scope`;
  const body = clauses.length === 0 ? `${lead}.` : `${lead}, including ${clauses.length === 1 ? clauses[0] : `${clauses.slice(0, -1).join(", ")}, and ${clauses[clauses.length - 1]}`}.`;

  // Phase 12 brief: never imply precision - an addition's actual cost depends heavily on
  // site-specific structural/foundation conditions that can't be assessed from a questionnaire.
  return `${body} Actual cost can vary significantly depending on site conditions and what's found once construction begins.`;
}

const WHOLE_HOME_FLOORS_LABELS: Record<string, string> = {
  "1": "1 floor",
  "1_5": "1.5 floors",
  "2": "2 floors",
  "3_plus": "3+ floors"
};

const WHOLE_HOME_WINDOWS_DOORS_CLAUSES: Record<string, string> = {
  some_replacement: "some window/door replacement",
  full_replacement: "full window/door replacement"
};

const WHOLE_HOME_FLOORING_CLAUSES: Record<string, string> = {
  partial_replacement: "flooring partially replaced",
  full_replacement: "flooring replaced throughout"
};

const WHOLE_HOME_ELECTRICAL_CLAUSES: Record<string, string> = {
  minor_updates: "minor electrical updates",
  partial_upgrade: "a partial electrical upgrade",
  full_rewire_panel_upgrade: "a full rewire and panel upgrade"
};

const WHOLE_HOME_PLUMBING_CLAUSES: Record<string, string> = {
  minor_updates: "minor plumbing updates",
  partial_repipe: "a partial re-pipe",
  full_repipe: "a full re-pipe throughout"
};

const WHOLE_HOME_HVAC_CLAUSES: Record<string, string> = {
  upgrade_existing: "an upgraded HVAC system",
  new_system: "a new HVAC system"
};

const WHOLE_HOME_EXTERIOR_CLAUSES: Record<string, string> = {
  minor: "minor exterior work",
  major: "major exterior work"
};

/**
 * Phase 20 audit fix: `whole_home_renovation`, like `addition`, had no
 * dedicated summary builder and was falling through to the generic
 * fallback. Whole-home has by far the most category toggles of any project
 * type (see rules/whole-home.ts and questions/whole-home.ts), so this only
 * surfaces a category when it was actually included/changed - "no change"
 * selections (keep_existing, none, single_phase, vacant) are deliberately
 * omitted, same rationale as bathroom's "keep_existing" omission above.
 * Floor count and occupancy/phasing (real cost/risk drivers per
 * contingency.ts) are always surfaced when answered.
 */
export function buildWholeHomePlainLanguageSummary(input: EstimatorInput): string {
  const clauses: string[] = [];

  if (input.rawAnswers["whole_home_kitchen_included"] === true) clauses.push("the kitchen");
  if (input.rawAnswers["whole_home_basement_included"] === true) clauses.push("the basement");

  const windowsDoors = input.rawAnswers["whole_home_windows_doors_scope"];
  if (typeof windowsDoors === "string" && WHOLE_HOME_WINDOWS_DOORS_CLAUSES[windowsDoors]) {
    clauses.push(WHOLE_HOME_WINDOWS_DOORS_CLAUSES[windowsDoors]!);
  }

  const flooring = input.rawAnswers["whole_home_flooring_scope"];
  if (typeof flooring === "string" && WHOLE_HOME_FLOORING_CLAUSES[flooring]) clauses.push(WHOLE_HOME_FLOORING_CLAUSES[flooring]!);

  const electrical = input.rawAnswers["whole_home_electrical_scope"];
  if (typeof electrical === "string" && WHOLE_HOME_ELECTRICAL_CLAUSES[electrical]) clauses.push(WHOLE_HOME_ELECTRICAL_CLAUSES[electrical]!);

  const plumbing = input.rawAnswers["whole_home_plumbing_scope"];
  if (typeof plumbing === "string" && WHOLE_HOME_PLUMBING_CLAUSES[plumbing]) clauses.push(WHOLE_HOME_PLUMBING_CLAUSES[plumbing]!);

  const hvac = input.rawAnswers["whole_home_hvac_scope"];
  if (typeof hvac === "string" && WHOLE_HOME_HVAC_CLAUSES[hvac]) clauses.push(WHOLE_HOME_HVAC_CLAUSES[hvac]!);

  const exterior = input.rawAnswers["whole_home_exterior_scope"];
  if (typeof exterior === "string" && WHOLE_HOME_EXTERIOR_CLAUSES[exterior]) clauses.push(WHOLE_HOME_EXTERIOR_CLAUSES[exterior]!);

  if (input.rawAnswers["whole_home_occupancy_status"] === "occupied") clauses.push("the home occupied during construction");
  else if (input.rawAnswers["whole_home_occupancy_status"] === "partially_occupied") clauses.push("the home partially occupied during construction");

  if (input.rawAnswers["whole_home_phasing"] === "phased") clauses.push("the work phased over time rather than done all at once");

  if (input.accessibility.required === "yes") clauses.push("accessibility/mobility accommodations");

  const floors = input.rawAnswers["whole_home_floors"];
  const floorsLabel = typeof floors === "string" ? WHOLE_HOME_FLOORS_LABELS[floors] : undefined;
  const sizeLabel = input.roomSize ? `${input.roomSize.value} sq. ft.` : "unspecified-size";
  const homeLabel = floorsLabel ? `${sizeLabel} home (${floorsLabel})` : `${sizeLabel} home`;
  const scopeLabel = (SCOPE_LABELS[input.scope] ?? input.scope).toLowerCase();

  const lead = `Your estimate is based on a whole-home renovation of a ${homeLabel} in ${input.location.province} with a ${scopeLabel} scope`;
  if (clauses.length === 0) return `${lead}.`;

  const joined = clauses.length === 1 ? clauses[0] : `${clauses.slice(0, -1).join(", ")}, and ${clauses[clauses.length - 1]}`;
  return `${lead}, including ${joined}.`;
}
